import { ReactNode, useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  ColorValue,
  InputModeOptions,
  KeyboardTypeOptions,
  Platform,
  StyleProp,
  StyleSheet,
  ViewStyle,
} from 'react-native';
import { TextInput } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { AutocompleteTextInput } from './formTypes';

type ControlledTextInputProps = {
  control: any;
  name: string;
  placeholder?: string;
  editable?: boolean;
  autoFocus?: boolean;
  textArea?: boolean;
  textColor?: string;
  innerRef?: any;
  showClearValue?: boolean;
  outlinedTextInput?: boolean;
  keyboardType?: KeyboardTypeOptions;
  autoComplete?: AutocompleteTextInput;
  height?: number;
  disableInnerStyle?: boolean;
  testID?: string;
  isLowerCase?: boolean;
  backgroundColor?: ColorValue;
  width?: number | '100%';
  inputInnerStyle?: StyleProp<ViewStyle>;
  onFocus?: () => void;
};

export const ControlledTextInput = (props: ControlledTextInputProps) => {
  const editable = props.editable === undefined ? true : props.editable;

  return (
    <Controller
      control={props.control}
      name={props.name}
      render={({ field: { onChange, onBlur, value } }) => (
        <StyledTextInput
          testID={props.testID}
          onChangeText={(text) =>
            onChange(props?.isLowerCase ? text.toLowerCase() : text)
          }
          onBlur={onBlur}
          value={value}
          placeholder={props.placeholder}
          editable={editable}
          autoFocus={props.autoFocus}
          textArea={props.textArea}
          textColor={props.textColor}
          innerRef={props.innerRef}
          right={iconClearValue({
            showClearValue: props.showClearValue,
            value,
            onChange,
          })}
          outlinedTextInput={props.outlinedTextInput}
          keyboardType={props.keyboardType}
          autoComplete={props.autoComplete}
          height={props.height}
          disableInnerStyle={props.disableInnerStyle}
          backgroundColor={props.backgroundColor}
          width={props.width}
          inputInnerStyle={props.inputInnerStyle}
          onFocus={() => props?.onFocus?.()}
        />
      )}
    />
  );
};

export type StyledTextInputProps = Omit<
  ControlledTextInputProps,
  'control' | 'name'
> & {
  onChangeText: (text: string) => void;
  onBlur: () => void;
  onFocus?: () => void;
  onSubmitEditing?: () => void;
  value?: any;
  left?: ReactNode;
  right?: ReactNode;
  outlineStyle?: StyleProp<ViewStyle>;
  disableInnerStyle?: boolean;
  height?: number;
  textColor?: string;
  keyboardType?: KeyboardTypeOptions;
  autoComplete?: AutocompleteTextInput;
  width?: number | '100%';
  inputMode?: InputModeOptions | undefined;
  backgroundColor?: ColorValue;
  inputInnerStyle?: StyleProp<ViewStyle>;
  pointerEvents?: 'none' | 'auto' | 'box-none' | 'box-only';
  render?: any;
  style?: StyleProp<any>;
  returnKeyType?: 'done' | 'go' | 'next' | 'search' | 'send';
  disabled?: boolean;
  label?: string;
};

export const StyledTextInput = (props: StyledTextInputProps) => {
  const editable = props.editable === undefined ? true : props.editable;
  const [isFocused, setIsFocused] = useState(false);

  return (
    <TextInput
      label={props.label}
      testID={props.testID}
      left={props.left}
      right={props.right}
      mode="outlined"
      textColor={props.textColor}
      style={[
        {
          height: props.height || 55,
          width: props.width || 'auto',
          backgroundColor: props?.backgroundColor ?? Colors.white,
        },
        props.outlinedTextInput && {
          borderWidth: 1,
          borderColor: isFocused ? Colors.black : Colors.tabInactive,
          borderRadius: 8,
        },
        props?.style ?? {},
      ]}
      contentStyle={[
        props.disableInnerStyle
          ? undefined
          : props.textArea
          ? [
              styledTextInputStyles.baseInputContent,
              styledTextInputStyles.areaInputContent,
            ]
          : [styledTextInputStyles.baseInputContent],
        Platform.select({
          android: props.textArea
            ? {
                paddingTop: 12,
                paddingBottom: 12,
              }
            : {
                paddingTop: 0,
                paddingBottom: 0,
              },
        }),
        props.inputInnerStyle,
      ]}
      value={props.value || ''}
      multiline={props.textArea}
      inputMode={props.inputMode}
      outlineStyle={
        props.outlinedTextInput
          ? undefined
          : props.outlineStyle ?? styledTextInputStyles.textOutline
      }
      onBlur={(e) => {
        setIsFocused(false);
        props.onBlur && props.onBlur();
      }}
      onFocus={(e) => {
        setIsFocused(true);
        props.onFocus && props.onFocus();
      }}
      onSubmitEditing={props.onSubmitEditing}
      onChangeText={(value) => props.onChangeText(value)}
      autoFocus={props.autoFocus}
      placeholder={props.placeholder}
      editable={editable}
      placeholderTextColor={Colors.grayDisabled}
      ref={props.innerRef}
      keyboardType={props.keyboardType}
      autoComplete={props.autoComplete}
      pointerEvents={props.pointerEvents}
      render={props.render}
      returnKeyType={props.returnKeyType}
      disabled={props.disabled}
    />
  );
};

const iconClearValue = ({
  showClearValue = false,
  value,
  onChange,
}: {
  showClearValue?: boolean;
  value: any;
  onChange: (...event: any[]) => void;
}) =>
  showClearValue && (
    <TextInput.Icon
      icon="close"
      color={value?.length ? Colors.primaryDark : Colors.grayText}
      onPress={() => onChange('')}
    />
  );

export const styledTextInputStyles = StyleSheet.create({
  areaInputContent: {
    height: 100,
    paddingTop: 12,
  },
  baseInputContent: {
    paddingHorizontal: 20,
    backgroundColor: '#fff',
    borderRadius: 8,
    width: 'auto',
  },
  textOutline: {
    borderWidth: 0,
  },
  counterButton: {
    width: 64,
    height: '100%',
    backgroundColor: Colors.white,
    borderRadius: 0,
    borderWidth: 0,
    margin: 0,
  },
});
